import type { Registry as Services } from '@ember/service';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import switchAccount from 'lumen/utils/hotel-and-market/switch-account';
import type {
  Account,
  AccountPage,
  AccountSelectorProps,
} from 'shared-react/lumen';

interface AccessibleEntity {
  brand_code?: string;
  destination_subscription_id?: number | string | null;
  hotelinfo_id?: number;
  market_id?: number | string | null;
  name: string;
  owner_email?: string | null;
  plans?: string[];
  products?: string[];
  subscription_ids?: number[];
  type: 'hotel' | 'hotelinfo' | 'market' | 'destinationsubscription';
}

interface AccessibleEntitiesResponse {
  meta: {
    results_count: number;
    results_page: number;
    results_per_page: number;
  };
  results: AccessibleEntity[];
}

interface CreateAccountSelectorOptions {
  access: Services['access'];
  ajax: Services['ajax'];
  current: Account;
  currentMarketId?: string;
  errorTracking: Services['error-tracking'];
  isDemoMode?: boolean;
  router: RouterServiceWithLegacyTransitionTo;
}

function toTypeAndId(
  entity: AccessibleEntity,
): Pick<Account, 'type' | 'id'> | undefined {
  switch (entity.type) {
    case 'market':
      return entity.market_id == null
        ? undefined
        : { type: 'market', id: String(entity.market_id) };

    case 'destinationsubscription':
      return entity.destination_subscription_id == null
        ? undefined
        : {
            type: 'destination',
            id: String(entity.destination_subscription_id),
          };

    case 'hotel':
    case 'hotelinfo': {
      const subscriptionId = entity.subscription_ids?.[0];
      return subscriptionId == null
        ? undefined
        : { type: 'hotel', id: String(subscriptionId) };
    }

    default:
      return entity.type satisfies never;
  }
}

function toAccount(entity: AccessibleEntity): Account | undefined {
  const typeAndId = toTypeAndId(entity);

  if (typeAndId === undefined || !entity.name) {
    return undefined;
  }

  return {
    ...typeAndId,
    hotelinfoId: entity.hotelinfo_id,
    name: entity.name,
    brandCode: entity.brand_code,
    ownerEmail: entity.owner_email ?? undefined,
    plans: entity.plans ?? [],
    products: entity.products ?? [],
  };
}

function toAccountPage(response: AccessibleEntitiesResponse): AccountPage {
  const accounts: Account[] = [];

  for (const entity of response.results) {
    const account = toAccount(entity);
    if (account !== undefined) {
      accounts.push(account);
    }
  }

  const { meta } = response;

  return {
    accounts,
    hasMore:
      (meta.results_page + 1) * meta.results_per_page < meta.results_count,
  };
}

/**
 * Backs the React header's account selector with lumen's accessible-entities
 * endpoint and its own navigation rules.
 */
export default function createAccountSelector({
  current,
  ajax,
  router,
  access,
  errorTracking,
  isDemoMode,
  currentMarketId,
}: CreateAccountSelectorOptions): AccountSelectorProps {
  return {
    current,
    isDemoMode,
    isStaff: access.isStaffUser,
    onSelect: (account): void => {
      switchAccount({ type: account.type, id: account.id }, { access, router });
    },
    search: async (searchTerm, page): Promise<AccountPage> => {
      const url = `${ajax.host}/apigateway/v1/app/users/me/accessible-entities/`;

      try {
        const response = await ajax.request<AccessibleEntitiesResponse>(url, {
          method: 'GET',
          data: { q: searchTerm, page, market_id: currentMarketId },
        });

        return toAccountPage(response);
      } catch (error) {
        errorTracking.captureException(error);
        throw error;
      }
    },
  };
}
